import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import useDocumentTitle from '@hooks/useDocumentTitle';

import ResetPassword from '@features/client/components/ResetPassword';

import { fetchCategories, fetchDepartments, fetchEnumValues, fetchTeamBrands } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { printApiError } from '@utils/Util';

const EditClient = () => {
	const [state, setState] = useState({
		id: '',
		name: '',
		password: '',
		passwordRe: '',
		email: '',
		status: '',
		role: '',
		managerId: '',
		executiveId: '',
		nameError: '',
		passwordError: '',
		passwordReError: '',
		emailError: '',
		statusError: '',
		roleError: '',
		departmentIdError: '',
		executiveIdError: '',
		brandError: '',
		marketError: '',
		detailedName: '',
		designation: '',
		brandDepartment: '',
		company: '',
		companyId: '',
		phone: '',
		address: '',
		linkedin: '',
		facebook: '',
		twitter: '',
		brandId: '',
		brandName: '',
		roleList: [],
		statusList: [],
		managerList: [],
		executiveList: [],
		brandList: [],
		markets: [],
		selectedMarket: [],
		categories: [],
		selectedCategories: [],
		suggestions: [],
		showSuggestion: false,
		openResetModal: false,
		passStrengthObj: {
			percentage: '0',
			color: '',
			text: ''
		},
		nickName: '',
		departments: [],
		teamBrands: [],
		teamId: null,
		loading: false
	});
	const [errors, setErrors] = useState({});

	const clientInfoRef = useRef();
	const wrapperRef = useRef();
	const { id } = useParams();
	const navigate = useNavigate();

	useDocumentTitle('Edit client - Nitex - The easiest clothing manufacturing software');

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		setData();

		setState((prevState) => ({
			...prevState,
			loading: true,
			id
		}));

		const fetchData = async () => {
			try {
				const marketData = await fetchDepartments();
				const categoriesData = await fetchCategories();
				const departmentsData = await fetchEnumValues(
					'info.nitex.app.user.BrandDepartment?sorted=true'
				);

				setState((prevState) => ({
					...prevState,
					markets: marketData?.map((item) => ({ label: item.name, value: item.id })),
					categories: categoriesData?.map((item) => ({ label: item.name, value: item.id })),
					departments: departmentsData || []
				}));

				const brandData = await ApiClient.GET('getBrandBasicList', '?page=0&size=1000');
				setState((prevState) => ({
					...prevState,
					brandList: brandData.data.data
				}));

				const clientDetails = await ApiClient.GET('getClientDetails', id);
				if (clientDetails.data) {
					const data = clientDetails.data;
					setState((prevState) => ({
						...prevState,
						loading: false,
						email: data.email || '',
						name: data.fullName || '',
						status: data.status || '',
						phone: data.phone || '',
						brandDepartment: data.brandDepartment || '',
						companyId: data.companyId || '',
						company: data.companyName || '',
						designation: data.designation || '',
						address: data.address || '',
						facebook: data.facebook || '',
						twitter: data.twitter || '',
						linkedin: data.linkedin || '',
						role: data.role || '',
						managerId: data.managerId || '',
						executiveId: data.executiveId || '',
						brandName: data.brandName || '',
						brandId: data.brandId || '',
						nickName: data.nickName || '',
						selectedMarket:
							data.marketList?.map((item) => ({
								value: item.id,
								label: item.name
							})) || [],
						selectedCategories:
							data.categoryList?.map((item) => ({
								value: item.id,
								label: item.name
							})) || [],
						teamId: data.teamId
					}));
					if (data.brandDepartment) {
						await onFetchDepartments(data);
					}
				} else {
					setState((prevState) => ({ ...prevState, loading: false }));
				}
			} catch (error) {
				setState((prevState) => ({ ...prevState, loading: false }));
				printApiError(error);
			}
		};

		fetchData();

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [id]);

	const handleClickOutside = (event) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
			setState((prevState) => ({ ...prevState, showSuggestion: false }));
		}
	};

	const setData = async () => {
		try {
			const statusData = await ApiClient.GET('getStatus');
			setState((prevState) => ({ ...prevState, statusList: statusData.data }));

			const managerData = await ApiClient.GET('getManagers');
			setState((prevState) => ({ ...prevState, managerList: managerData.data }));

			const executiveData = await ApiClient.GET('getExecutives');
			const combinedExecutives = executiveData.data.concat(managerData.data);
			setState((prevState) => ({ ...prevState, executiveList: combinedExecutives }));
		} catch (error) {
			printApiError(error);
		}
	};

	const onFetchDepartments = async (data) => {
		const teamBrandsData = await fetchTeamBrands(`${data.brandId}?department=${data.brandDepartment}`);
		setState((prevState) => ({ ...prevState, teamBrands: teamBrandsData || [] }));
	};

	const fetchSuggestions = async () => {
		await ApiClient.GET('getCompanyList', '?name=' + this.state.company)
			.then(({ data }) => {
				if (!data.length) {
					setState((prev) => ({ ...prev, companyId: '' }));
				}
				setState((prev) => ({ ...prev, suggestions: data }));
			})
			.catch((error) => {
				setState((prev) => ({ ...prev, loading: false }));
				printApiError(error);
			});
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		if (name === 'password') setPassPercentage(value);
		if (name === 'company') fetchSuggestions();

		setState((prevState) => ({
			...prevState,
			[name]: value
		}));
		resetValidation(name, value);
	};

	const setPassPercentage = (val) => {
		let percentage = 0;
		if (/\d/.test(val)) percentage += 25;
		if (/(?=.*[A-Z])/.test(val)) percentage += 25;
		if (val.length > 5) percentage += 50;

		let passStrengthObj = {};
		if (percentage < 50) {
			passStrengthObj = {
				percentage,
				color: 'red',
				text: 'Weak'
			};
		} else if (percentage < 75) {
			passStrengthObj = {
				percentage,
				color: 'yellow',
				text: 'Medium'
			};
		} else {
			passStrengthObj = {
				percentage,
				color: 'green',
				text: 'Strong'
			};
		}

		setState((prev) => ({
			...prev,
			passStrengthObj
		}));
	};

	const validate = () => {
		let errorObj = {};
		let re = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}/;
		let reEmail =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.]*)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

		if (state.email === '') {
			errorObj.emailError = 'Email is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -890);
		} else if (!reEmail.test(state.email)) {
			errorObj.emailError = 'Invalid email format!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -890);
		}

		if (!state.name) {
			errorObj.nameError = 'Name is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -850);
		}

		if (state.password !== '' && !re.test(state.password)) {
			errorObj.passwordError =
				'Password must contain 6 characters, at least one number, one lowercase and one uppercase letter';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}
		if (state.passwordRe !== state.password) {
			errorObj.passwordReError = "Passwords didn't match!";
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}

		if (!state.status) {
			errorObj.statusError = 'Status is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -450);
		}
		// if (!state.brandDepartment) {
		// 	errorObj.departmentIdError = 'Please select a department';
		// 	clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
		// 	window.scrollBy(0, -0);
		// }

		if (!state.executiveId) {
			errorObj.executiveIdError = 'Please select an executive';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -450);
		}

		if (!state.brandId) {
			errorObj.brandError = 'Brand is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}

		if (!(state.selectedMarket.length > 0)) {
			errorObj.marketError = 'Market is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const resetValidation = (fieldName, value) => {
		let isDataExist;
		if (Array.isArray(value)) {
			isDataExist = fieldName !== undefined && value.length > 0;
		} else {
			isDataExist = fieldName !== undefined && value.toString().length > 0;
		}

		if (isDataExist) {
			switch (fieldName) {
				case 'email':
					removeErrorFlag('emailError');
					break;
				case 'name':
					removeErrorFlag('nameError');
					break;
				case 'brandId':
					removeErrorFlag('brandError');
					break;
				case 'password':
					removeErrorFlag('passwordError');
					break;
				case 'passwordRe':
					removeErrorFlag('passwordReError');
					break;
				case 'status':
					removeErrorFlag('statusError');
					break;
				case 'departmentId':
					removeErrorFlag('departmentIdError');
					break;
				case 'executiveId':
					removeErrorFlag('executiveIdError');
					break;
				default:
					break;
			}
		}
	};

	const removeErrorFlag = (flagName) => {
		setState((prev) => ({
			...prev,
			[flagName]: ''
		}));
	};

	const submit = async () => {
		if (validate()) {
			setState((prev) => ({ ...prev, loading: true }));

			let body = {
				fullName: state.name,
				email: state.email,
				status: state.status,
				nickName: state.nickName,
				managerId: state.managerId,
				executiveId: state.executiveId,
				brandId: state.brandId,
				phone: state.phone,
				designation: state.designation,
				address: state.address,
				linkedin: state.linkedin,
				facebook: state.facebook,
				twitter: state.twitter,
				brandDepartment: state.brandDepartment,
				teamId: state.teamId ? state.teamId : null,
				marketIds: state.selectedMarket?.map((item) => item.value),
				categoryIds: state.selectedCategories?.map((item) => item.value),
				brandTeamId: state.teamId ? state.teamId : null
			};

			if (state.companyId) {
				body.companyId = state.companyId;
			} else {
				body.companyName = state.company;
			}

			if (state.password) {
				body.password = state.password;
				body.retypePassword = state.passwordRe;
			}

			await ApiClient.POST('editClient', body, state.id)
				.then(({ data }) => {
					setState((prev) => ({ ...prev, loading: false }));
					if (data.success) {
						toastSuccess(data.message);
						navigate('/clients/list');
					} else {
						toastError(data.message);
					}
				})
				.catch((error) => {
					setState((prev) => ({ ...prev, loading: false }));
					printApiError(error);
				});
		}
	};

	const triggerResetModal = () => {
		setState((prev) => ({
			...prev,
			openResetModal: !prev.openResetModal
		}));
	};

	const onChangeMultiSelect = (selected, fieldName) => {
		setState((prev) => ({
			...prev,
			[fieldName]: selected
		}));
	};

	const setCompany = (item) => {
		setState({
			...state,
			company: item.name,
			companyId: item.id,
			showSuggestion: false
		});
	};

	return (
		<LoaderComponent loading={state.loading}>
			<section className="project-profile">
				<div className="new-collection mt-4 edit-client">
					<div className="new-collection-form pt-0" ref={clientInfoRef}>
						<div className="form-header pb-3">
							<p className="semibold-16 mb-0">Edit client</p>
						</div>
						<div className="row">
							<div className="col-lg-12 mb-2">
								<h3 className="semibold-16">Client Info</h3>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Full name*</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										name="name"
										value={state.name}
										onChange={onChange}
										placeholder="Enter full name"
									/>
									{errors.nameError && <span className="error">{errors.nameError}</span>}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Nickname</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										name="nickName"
										value={state.nickName}
										onChange={onChange}
										placeholder="Enter nickname"
									/>
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Email*</label>
									<input
										className="bg-gray-light border-0"
										type="email"
										autoComplete="off"
										name="email"
										value={state.email}
										onChange={onChange}
										placeholder="Enter email address"
									/>
									{errors.emailError && <span className="error">{errors.emailError}</span>}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Phone number</label>
									<input
										type="text"
										autoComplete="off"
										className="bg-gray-light border-0"
										placeholder="Enter phone number"
										name="phone"
										value={state.phone}
										onChange={onChange}
									/>
								</div>
							</div>
							<div className="col-lg-12">
								<div className="form-group mb-0">
									<div className="input-group">
										<label>Linkedin profile</label>
										<input
											className="form-control bg-gray-light border-0"
											type="text"
											autoComplete="off"
											placeholder="Enter linkedin profile"
											name="linkedin"
											value={state.linkedin}
											onChange={onChange}
										/>
									</div>
								</div>
							</div>
							<div className="col-lg-12">
								<div className="form-group">
									<label>Brand*</label>
									<select
										name="brandId"
										onChange={onChange}
										value={state.brandId}
										className="bg-gray-light"
									>
										<option>Select brand</option>
										{state.brandList.map((item) => (
											<option key={item.id} value={item.id}>
												{item.name}
											</option>
										))}
									</select>
									{errors.brandError && <span className="error">{errors.brandError}</span>}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group no-shadow-react-select">
									<label htmlFor="operationalZone">Market*</label>
									<Select
										onChange={(selected) =>
											onChangeMultiSelect(selected, 'selectedMarket')
										}
										value={state.selectedMarket}
										name="market"
										isMulti
										options={state.markets}
									/>
									{errors.marketError && (
										<span className="error">{errors.marketError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group no-shadow-react-select">
									<label htmlFor="operationalZone">Category</label>
									<Select
										onChange={(selected) =>
											onChangeMultiSelect(selected, 'selectedCategories')
										}
										value={state.selectedCategories}
										name="category"
										isMulti
										options={state.categories}
									/>
								</div>
							</div>
						</div>
						{/* Additional Sections */}
						<div className="row">
							<div className="col-lg-12 mb-2">
								<h3 className="semibold-16">Admin Activity</h3>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Status*</label>
									<select
										name="status"
										value={state.status}
										onChange={onChange}
										className="bg-gray-light"
									>
										{state.statusList.map((item) => (
											<option key={item.key} value={item.key}>
												{item.value}
											</option>
										))}
									</select>
									{errors.statusError && (
										<span className="error">{errors.statusError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group mb-0">
									<label>Account manager*</label>
									<select
										name="executiveId"
										value={state.executiveId}
										onChange={onChange}
										className="bg-gray-light"
									>
										<option>Select manager</option>
										{state.executiveList.map((item) => (
											<option key={item.id} value={item.id}>
												{item.name}
											</option>
										))}
									</select>
									{errors.executiveIdError && (
										<span className="error">{errors.executiveIdError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-12">
								<a href="#" onClick={triggerResetModal} className="regular-14">
									Reset Password
								</a>
							</div>
							{state.openResetModal && (
								<ResetPassword
									openModal={state.openResetModal}
									onCloseModal={triggerResetModal}
								/>
							)}
						</div>
						<div className="row">
							<div className="col-lg-12 mb-2">
								<h4 className="semibold-16">Detailed information</h4>
							</div>
							<div className="col-lg-12">
								<div className="form-group">
									<label>Company</label>
									<input
										type="text"
										className="bg-gray-light border-0"
										placeholder="Enter company"
										name="company"
										value={state.company}
										onChange={onChange}
										onClick={() =>
											setState((prev) => ({ ...prev, showSuggestion: true }))
										}
										autoComplete="off"
									/>
									{state.showSuggestion && (
										<div className="suggest-filed" ref={wrapperRef}>
											<ul>
												{state.suggestions.length ? (
													state.suggestions.map((item) => (
														<li key={item.id} onClick={() => setCompany(item)}>
															{item.name}
														</li>
													))
												) : (
													<li
														onClick={() =>
															setState((prev) => ({
																...prev,
																showSuggestion: false
															}))
														}
													>
														No suggestions found...
													</li>
												)}
											</ul>
										</div>
									)}
								</div>
							</div>
							<div className="col-lg-12">
								<div className="form-group">
									<label>Designation</label>
									<input
										type="text"
										autoComplete="off"
										className="bg-gray-light border-0"
										placeholder="Enter designation"
										name="designation"
										value={state.designation}
										onChange={onChange}
									/>
								</div>
							</div>
							<div className="col-lg-12">
								<div className="form-group mb-0">
									<label>Address</label>
									<textarea
										className="bg-gray-light border-0"
										cols="30"
										rows="10"
										placeholder="Enter address..."
										name="address"
										value={state.address}
										onChange={onChange}
									/>
								</div>
							</div>
						</div>
						<div className="d-flex justify-content-end mt-0">
							<button className="button" onClick={submit}>
								Update
							</button>
						</div>
					</div>
				</div>
			</section>
		</LoaderComponent>
	);
};

export default EditClient;
